import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  getStoredAccessToken,
  getProfile,
  login as apiLogin,
  logout as apiLogout,
  ProfileResponse,
} from '../api/client';

interface AuthState {
  user: ProfileResponse | null;
  loading: boolean;
  error: string | null;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string, turnstileToken?: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  });

  const refreshUser = useCallback(async () => {
    const token = getStoredAccessToken();
    if (!token) {
      setState({ user: null, loading: false, error: null });
      return;
    }
    try {
      const user = await getProfile();
      setState({ user, loading: false, error: null });
    } catch {
      setState({ user: null, loading: false, error: null });
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = useCallback(async (email: string, password: string, turnstileToken?: string) => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      await apiLogin({
        email,
        password,
        turnstile_token: turnstileToken,
      });
      await refreshUser();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Giriş başarısız';
      setState((s) => ({ ...s, loading: false, user: null, error: message }));
      throw e;
    }
  }, [refreshUser]);

  const logout = useCallback(() => {
    apiLogout();
    setState({ user: null, loading: false, error: null });
  }, []);

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
